/**
 * Draaidagen-overzicht — de vervanger van de Airtable-interface
 * "Planning → Draaidagen" en "Buitenlandshoots".
 *
 * Leest live via de backend uit DraaidagenOutlook (formule-statussen incluis),
 * gegroepeerd zoals het team gewend is: vandaag → later deze week → per week.
 * De buitenland-weergave groepeert op jaar en bestaat omdat die shoots aan de
 * reisverzekering doorgegeven moeten worden.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, InputAdornment,
  MenuItem, Select, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Public as PublicIcon,
  EventNote as EventNoteIcon,
  CalendarViewWeek as CalendarViewWeekIcon,
} from '@mui/icons-material';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson } from '../../utils/apiFout';
import PageHeader from '../../ui-components/PageHeader';
import { StoplichtRij, fetchProjectStatussen } from '../../ui-components/ProjectStoplicht';
import { formatDateISO, getMonday, addDays } from '../../utils/weekUtils';
import DraaidagDetailDrawer from './DraaidagDetailDrawer';
import DraaidagenKalender from './DraaidagenKalender';
import WeekRegelBadge from './WeekRegelBadge';
import OpenRolSignaal from './OpenRolSignaal';
import { ambigueVoornamenUit, dagenVanRij, markeringVoorPersoon, rijInWeek } from './regelMarkering';
import { PLANNINGSTATUS_STIJL } from './planningstatusStijl';
import { normaliseerMedewerkers, plVoorRij } from '../../utils/draaidagPl';
import { openRollen, personenMetFunctie, wekenVan } from '../../utils/draaidagBeschikbaar';

export { PLANNINGSTATUS_STIJL };

const FONT = '"Manrope", system-ui, sans-serif';

export const DOT_KLEUR = { groen: '#16a34a', oranje: '#f59e0b', rood: '#dc2626', fout: '#dc2626' };

/** Tinten voor de naam-markering, per soort uit de week-check. */
const MARKERING_TOON = {
  over: { bg: '#fef2f2', fg: '#b91c1c', chip: '#fee2e2' },
  op_max: { bg: '#fff7ed', fg: '#b45309', chip: '#ffedd5' },
  druk: { bg: '#fff7ed', fg: '#b45309', chip: '#ffedd5' },
  ruimte: { bg: '#f0fdf4', fg: '#166534', chip: '#dcfce7' },
};


/**
 * ISO-week mét ISO-jaar. Het weekjaar wijkt rond de jaarwisseling af van het
 * kalenderjaar (29 dec kan week 1 van het vólgende jaar zijn) — met alleen het
 * kalenderjaar werd één ISO-week in twee groepen geknipt (Codex-review).
 */
const isoWeek = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  const target = new Date(d.valueOf());
  target.setDate(target.getDate() + 3 - ((d.getDay() + 6) % 7));
  const jaar = target.getFullYear();
  const jan4 = new Date(jaar, 0, 4);
  const week = 1 + Math.round(((target - jan4) / 86400000 - 3 + ((jan4.getDay() + 6) % 7)) / 7);
  return { week, jaar };
};
const weekNummer = (iso) => isoWeek(iso).week;

const WEEKDAG_KORT = ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'];
const weekdagLabel = (iso, dagen = 1) => {
  const d = new Date(`${iso}T12:00:00`);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  // Meerdaags: laat de hele reeks zien ("Di–Wo 08-09"), anders lijkt een
  // tweedaagse draaidag maar één dag te beslaan (Joris, 1 sep).
  if (Number(dagen) > 1 || (typeof dagen === 'string' && dagen.includes('-'))) {
    const e = typeof dagen === 'string' ? new Date(`${dagen}T12:00:00`) : new Date(d);
    if (typeof dagen !== 'string') e.setDate(e.getDate() + Number(dagen) - 1);
    return `${WEEKDAG_KORT[d.getDay()]}–${WEEKDAG_KORT[e.getDay()]} ${dd}-${mm}`;
  }
  return `${WEEKDAG_KORT[d.getDay()]} ${dd}-${mm}`;
};

/**
 * Groepeer aankomende draaidagen zoals de interface: recent (afgelopen week),
 * vandaag, later deze week, daarna per week. De koppen dragen het aantal mee.
 */
function groepeerAankomend(rows, vandaagISO) {
  const maandag = getMonday(new Date(`${vandaagISO}T12:00:00`));
  const eindeWeek = formatDateISO(addDays(maandag, 6));
  const groepen = [];
  const maak = (label, weekKey = null) => { const g = { label, rows: [], weekKey }; groepen.push(g); return g; };
  let recent = null, vandaag = null, laterDezeWeek = null;
  const perWeek = new Map();

  rows.forEach((r) => {
    const d = String(r.startDatum || '');
    if (!d) return;
    if (d < vandaagISO) {
      (recent = recent || maak('Afgelopen week')).rows.push(r);
    } else if (d === vandaagISO) {
      (vandaag = vandaag || maak(`Vandaag (${weekdagLabel(d)})`)).rows.push(r);
    } else if (d <= eindeWeek) {
      (laterDezeWeek = laterDezeWeek || maak(`Later deze week (Week ${weekNummer(d)})`, formatDateISO(maandag))).rows.push(r);
    } else {
      const { week: wk, jaar } = isoWeek(d);
      const key = `${jaar}-W${wk}`;
      if (!perWeek.has(key)) {
        // "Volgende week" via de maandag van volgende week — dat klopt ook over
        // een jaargrens heen (week 52 → week 1).
        const volgendeWeek = isoWeek(formatDateISO(addDays(maandag, 7)));
        const volgende = volgendeWeek.week === wk && volgendeWeek.jaar === jaar;
        perWeek.set(key, maak(volgende ? `Volgende week (Week ${wk})` : `Week ${wk} · ${jaar}`, formatDateISO(getMonday(new Date(`${d}T12:00:00`)))));
      }
      perWeek.get(key).rows.push(r);
    }
  });
  // Binnen een groep: op datum, dan eendaagse vóór meerdaagse (anders 'verdwijnt' een
  // eendaagse draaidag onder een tweedaagse met dezelfde startdag — ADP, 15 sep), dan naam.
  const meerdaags = (r) => (r.eindDatum && r.eindDatum > r.startDatum) || Number(r.dagen) > 1 ? 1 : 0;
  groepen.forEach((g) => g.rows.sort((a, b) =>
    String(a.startDatum).localeCompare(String(b.startDatum))
    || meerdaags(a) - meerdaags(b)
    || String(a.eindDatum || '').localeCompare(String(b.eindDatum || ''))
    || String(a.naam || a.titel || '').localeCompare(String(b.naam || b.titel || ''))));
  return groepen;
}

/** Sleutel voor "toon de datum opnieuw": startdag én reeks — na 'Di–Wo 29-09' hoort een
 *  losse 'Di 29-09' weer zichtbaar te zijn, anders lijkt die rij zonder datum. */
const datumSleutel = (r) => `${r.startDatum || ''}|${r.eindDatum && r.eindDatum > r.startDatum ? r.eindDatum : Number(r.dagen) > 1 ? r.dagen : 1}`;

/** Buitenland: per jaar, zoals de reisverzekerings-view. */
const groepeerPerJaar = (rows) => {
  const per = new Map();
  rows.forEach((r) => {
    const jaar = String(r.startDatum || '').slice(0, 4) || '?';
    if (!per.has(jaar)) per.set(jaar, { label: jaar, rows: [] });
    per.get(jaar).rows.push(r);
  });
  return [...per.values()].sort((a, b) => a.label.localeCompare(b.label));
};

function StatusDot({ dot, foutmelding, voorbereidingsstatus }) {
  const tip = foutmelding ? `FOUT: ${foutmelding}` : voorbereidingsstatus || 'Onbekend';
  return (
    <Tooltip title={tip} arrow>
      {dot === 'fout' ? (
        <Chip size="small" label="FOUT" sx={{ height: 18, fontSize: 10, fontWeight: 800, bgcolor: '#dc2626', color: '#fff' }} />
      ) : (
        <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: DOT_KLEUR[dot] || '#94a3b8', flexShrink: 0 }} />
      )}
    </Tooltip>
  );
}

function PlanningstatusBadge({ status }) {
  if (!status) return null;
  const stijl = PLANNINGSTATUS_STIJL[status] || { bg: '#f1f5f9', fg: '#475569' };
  return (
    <Chip size="small" label={status}
      sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: stijl.bg, color: stijl.fg, maxWidth: 190 }} />
  );
}

export default function DraaidagenOverzicht() {
  const navigate = useNavigate();
  const [scope, setScope] = useState('aankomend'); // 'aankomend' | 'buitenland'
  // Lijst of kalender (wiki-feedback: lanes per regisseur/cameraman). De keuze
  // blijft staan, want wie in de kalender werkt wil daar de volgende keer weer in.
  const [weergave, setWeergave] = useState(() => localStorage.getItem('draaidagen-weergave') || 'lijst');
  const [rows, setRows] = useState([]);
  const [stoplichten, setStoplichten] = useState({});
  const [weekChecks, setWeekChecks] = useState({});
  const [weekCheckOpen, setWeekCheckOpen] = useState(null); // weekKey van het opengeklapte week-check-paneel
  const weekCheckKnop = useRef(null); // de badge die het paneel opende (focus terug na Esc)
  const [weekDetail, setWeekDetail] = useState({}); // { anchor, weekKey }
  // Laatste request-id per weekKey:naam — een trage response van een inmiddels
  // ingeklapte of opnieuw opgevraagde persoon mag het detail niet overschrijven.
  const detailVerzoek = useRef({});
  // Naam in de week-check markeert zijn draaidagen in de tabel (Joris, 6 okt):
  // hover/focus = voorproefje, klik = vastzetten tot opnieuw klikken of Esc.
  const [markeringHover, setMarkeringHover] = useState(null); // { naam, weekKey, soort }
  const [markeringFocus, setMarkeringFocus] = useState(null); // los van hover: muis weg ≠ focus weg
  const [markeringVast, setMarkeringVast] = useState(null);
  const [agendaDetail, setAgendaDetail] = useState({}); // weekKey:naam → weekdetail, voor 'volle agenda'-dagen
  const [laden, setLaden] = useState(true);
  const [fout, setFout] = useState(null);
  const [zoek, setZoek] = useState('');
  // Kolombreedtes — Excel-achtig: sleep de rand van een kolomkop. Onthouden
  // in localStorage; dubbel-klik op een rand = terug naar standaard.
  const STANDAARD_BREEDTES = { draaidag: 340, project: 64, opdrachtgever: 150, pl: 110, crew: 150, status: 180 };
  const [kolomBreedte, setKolomBreedte] = useState(() => {
    try { return { ...STANDAARD_BREEDTES, ...(JSON.parse(localStorage.getItem('ff_draaidagen_kolommen')) || {}) }; }
    catch { return STANDAARD_BREEDTES; }
  });
  useEffect(() => { localStorage.setItem('ff_draaidagen_kolommen', JSON.stringify(kolomBreedte)); }, [kolomBreedte]);
  const sleepRef = useRef(null); // { kolom, startX, startBreedte }
  const startSleep = (kolom) => (e) => {
    e.preventDefault(); e.stopPropagation();
    sleepRef.current = { kolom, startX: e.clientX, startBreedte: kolomBreedte[kolom] };
    const move = (ev) => {
      const s = sleepRef.current;
      if (!s) return;
      const nieuw = Math.max(50, Math.min(900, s.startBreedte + (ev.clientX - s.startX)));
      setKolomBreedte((b) => ({ ...b, [s.kolom]: nieuw }));
    };
    const stop = () => {
      sleepRef.current = null;
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', stop);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', stop);
  };
  const w = kolomBreedte;
  const rijGrid = `30px 88px 46px ${w.draaidag}px ${w.project}px ${w.opdrachtgever}px ${w.pl}px ${w.crew}px ${w.status}px`;

  // 📺 Presentatiemodus: fullscreen op de pagina-container, filters weg, iets
  // groter — voor op een scherm; de 60s-autoverversing houdt hem actueel.
  const [presentatie, setPresentatie] = useState(false);
  const paginaRef = useRef(null);
  const togglePresentatie = async () => {
    try {
      if (!document.fullscreenElement) {
        await paginaRef.current?.requestFullscreen();
        setPresentatie(true);
      } else {
        await document.exitFullscreen();
      }
    } catch { setPresentatie((v) => !v); }
  };
  useEffect(() => {
    const sync = () => setPresentatie(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);
  const [crewFilter, setCrewFilter] = useState('');
  const [plFilter, setPlFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [gekozen, setGekozen] = useState(null); // draaidag-id voor het detailpaneel
  // Vangnet: draaidagen die in de tool zijn verwijderd (Verberg) maar nog in de Outlook-agenda staan
  const [verborgenNogInOutlook, setVerborgenNogInOutlook] = useState([]);
  const [terugzetBezig, setTerugzetBezig] = useState(null);
  // Medewerkers voor PL-uit-initialen ("- LJ" in de agendatitel) als Airtable geen PL heeft; fail-soft.
  const [medewerkers, setMedewerkers] = useState([]);
  // Alle actieve personen mét functie: welke rol (camera/regie) een crew-naam vult.
  const [crewPersonen, setCrewPersonen] = useState(null);
  useEffect(() => {
    authFetch(`${BACKEND_URL}/Api/personen/all`)
      .then((r) => (r.ok ? r.json() : {}))
      .then((dta) => {
        const lijst = dta.personen || dta.persons || dta.records || [];
        setMedewerkers(normaliseerMedewerkers(lijst));
        setCrewPersonen(personenMetFunctie(lijst));
      })
      .catch(() => {});
  }, []);

  // Wie is er vrij voor een open camera-/regierol (13 okt): één batch per week, pas bij
  // de eerste hover op zo'n week; daarna uit het geheugen (10 min, de engine cachet ook).
  const [beschikbaarheid, setBeschikbaarheid] = useState({}); // weekKey → { status, data, tijd }
  const beschikbaarheidVerzoek = useRef({});
  const laadBeschikbaarheid = useCallback((weekKeys) => {
    weekKeys.forEach((wk) => {
      const lopend = beschikbaarheidVerzoek.current[wk];
      if (lopend && (lopend.status === 'laden' || Date.now() - lopend.tijd < 10 * 60 * 1000)) return;
      beschikbaarheidVerzoek.current[wk] = { status: 'laden', tijd: Date.now() };
      setBeschikbaarheid((prev) => ({ ...prev, [wk]: { status: 'laden' } }));
      authFetch(`${BACKEND_URL}/Api/draaidagen-overzicht/beschikbaarheid?week=${wk}&rollen=camera,regie`)
        .then((r) => leesJson(r, 'Beschikbaarheid laden'))
        .then((d) => {
          beschikbaarheidVerzoek.current[wk] = { status: 'klaar', tijd: Date.now() };
          setBeschikbaarheid((prev) => ({ ...prev, [wk]: { status: 'klaar', data: d } }));
        })
        .catch(() => {
          // Mislukt: niet onthouden, de volgende hover probeert het opnieuw.
          beschikbaarheidVerzoek.current[wk] = { status: 'fout', tijd: 0 };
          setBeschikbaarheid((prev) => ({ ...prev, [wk]: { status: 'fout' } }));
        });
    });
  }, []);
  const wekenStatus = (keys) => {
    const lijst = keys.map((k) => beschikbaarheid[k]);
    if (lijst.some((w) => !w || w.status === 'laden')) return { status: 'laden' };
    if (lijst.some((w) => w.status === 'fout')) return { status: 'fout' };
    return { status: 'klaar', data: lijst.map((w) => w.data) };
  };

  const vandaagISO = useMemo(() => formatDateISO(new Date()), []);

  const laad = useCallback(async (s = scope, vers = false, stil = false) => {
    if (!stil) { setLaden(true); setFout(null); }
    try {
      // `stil` (minuutronde): ook niet meetellen in de laadbalk, net als de vervolgverzoeken.
      const r = await authFetch(`${BACKEND_URL}/Api/draaidagen-overzicht?scope=${s}${vers ? '&vers=1' : ''}`, { stil });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setRows(d.draaidagen || []);
      setVerborgenNogInOutlook(d.verborgenNogInOutlook || []);
      // Stoplichten per project (zelfde bolletjes als Overzicht per project, Joris 1 sep).
      const pids = [...new Set((d.draaidagen || []).map((r) => r.projectId).filter(Boolean))];
      if (pids.length) fetchProjectStatussen(pids, { stil }).then(setStoplichten).catch(() => {});
      // Week-check (regels + agenda's) voor het zichtbare bereik — apart en
      // fail-soft: de symbolen verschijnen zodra de check binnen is.
      const datums = (d.draaidagen || []).map((r) => r.startDatum).filter(Boolean).sort();
      if (datums.length) {
        const van = datums[0];
        const tot = datums[datums.length - 1];
        authFetch(`${BACKEND_URL}/Api/draaidagen-overzicht/week-check?start=${van}&end=${tot}`, { stil })
          .then((r2) => (r2.ok ? r2.json() : null))
          .then((d2) => d2 && setWeekChecks(d2.weeks || {}))
          .catch(() => {});
      }
    } catch (e) { if (!stil) setFout(e.message); }
    finally { if (!stil) setLaden(false); }
  }, [scope]);

  useEffect(() => { laad(scope); }, [scope, laad]);

  const zetTerug = useCallback(async (item) => {
    setTerugzetBezig(item.id);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/draaidagen-overzicht/${item.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ verbergen: false }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || d.detail || `HTTP ${r.status}`); }
      setVerborgenNogInOutlook((lijst) => lijst.filter((x) => x.id !== item.id));
      laad(scope, true, true);
    } catch (e) { setFout(`Terugzetten mislukt: ${e.message}`); }
    finally { setTerugzetBezig(null); }
  }, [laad, scope]);

  // "Live" verversen: wijzigt iemand iets in het project (andere PL, status),
  // dan komt dat vanzelf binnen — elke 60 s stil herladen zolang de tab
  // zichtbaar is, plus direct bij terugkeer naar het tabblad.
  useEffect(() => {
    const ververs = () => { if (document.visibilityState === 'visible') laad(scope, true, true); };
    const timer = setInterval(ververs, 60000);
    window.addEventListener('focus', ververs);
    return () => { clearInterval(timer); window.removeEventListener('focus', ververs); };
  }, [scope, laad]);

  // PL uit Airtable, anders uit de initialen in de agendatitel (zie utils/draaidagPl).
  const rowsMetPl = useMemo(() => rows.map((r) => {
    const p = plVoorRij(r, medewerkers);
    return { ...r, pl: p.tekst, plUitTitel: p.uitTitel, plZeker: p.zeker, plInitialen: p.initialen, plKandidaten: p.kandidaten };
  }), [rows, medewerkers]);

  // Filteropties uit de data zelf — geen aparte endpoints nodig.
  const crewOpties = useMemo(() => [...new Set(rowsMetPl.flatMap((r) => r.crew))].sort(), [rowsMetPl]);
  const plOpties = useMemo(() => [...new Set(rowsMetPl.map((r) => r.pl).filter(Boolean))].sort(), [rowsMetPl]);
  const projectOpties = useMemo(() => {
    const m = new Map();
    // String() overal: het projectnummer kwam uit de Airtable-lookup als getal
    // en een number heeft geen .localeCompare — dat was een witte pagina.
    rowsMetPl.forEach((r) => { if (r.projectnummer) m.set(String(r.projectnummer), `${r.projectnummer} · ${r.opdrachtgever || ''}`); });
    return [...m.entries()].sort((a, b) => String(b[0]).localeCompare(String(a[0])));
  }, [rowsMetPl]);

  const gefilterd = useMemo(() => {
    const q = zoek.trim().toLowerCase();
    return rowsMetPl.filter((r) => {
      if (crewFilter && !r.crew.includes(crewFilter)) return false;
      if (plFilter && r.pl !== plFilter) return false;
      if (projectFilter && String(r.projectnummer) !== projectFilter) return false;
      if (q && !`${r.titel} ${r.opdrachtgever} ${r.projectnummer || ''} ${r.pl} ${r.crew.join(' ')}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rowsMetPl, zoek, crewFilter, plFilter, projectFilter]);

  const groepen = useMemo(
    () => (scope === 'buitenland' ? groepeerPerJaar(gefilterd) : groepeerAankomend(gefilterd, vandaagISO)),
    [gefilterd, scope, vandaagISO],
  );

  // Open camera-/regierol per komende draaidag (pas zodra de personen er zijn: anders
  // zou elke rij heel even "camera? regie?" tonen).
  const openPerRij = useMemo(() => {
    const m = new Map();
    if (!crewPersonen || scope === 'buitenland') return m;
    gefilterd.forEach((r) => {
      if (String(r.eindDatum || r.startDatum || '') < vandaagISO) return;
      const o = openRollen(r, crewPersonen);
      if (o.open.length) m.set(r.id, { ...o, datums: dagenVanRij(r) });
    });
    return m;
  }, [gefilterd, crewPersonen, scope, vandaagISO]);

  const markeringDoel = markeringHover || markeringFocus || markeringVast;
  const markering = useMemo(() => {
    if (!markeringDoel) return null;
    const sleutel = `${markeringDoel.weekKey}:${markeringDoel.naam}`;
    const detail = [weekDetail[sleutel], agendaDetail[sleutel]].find((d) => d && Array.isArray(d.dagen)) || null;
    // Meerduidige voornamen: week-check-personen plus alle actieve medewerkers —
    // liever een losse "Robin" niet markeren dan de verkeerde Robin.
    const doel = { ...markeringDoel,
      ambigueVoornamen: ambigueVoornamenUit(weekChecks[markeringDoel.weekKey], medewerkers.map((m) => m.naam)) };
    return { ...markeringVoorPersoon(gefilterd, doel, detail), weekKey: markeringDoel.weekKey };
  }, [markeringDoel, gefilterd, weekDetail, agendaDetail, weekChecks, medewerkers]);
  // 'Volle agenda': welke dagen precies vol zitten staat in het weekdetail — haal
  // het stil op zodra zo'n naam wordt aangewezen (fail-soft, één keer per persoon/week).
  useEffect(() => {
    if (!markeringDoel || markeringDoel.soort !== 'druk') return;
    const sleutel = `${markeringDoel.weekKey}:${markeringDoel.naam}`;
    if (agendaDetail[sleutel] || weekDetail[sleutel]?.dagen) return;
    setAgendaDetail((prev) => ({ ...prev, [sleutel]: { laden: true } }));
    const params = new URLSearchParams({ naam: markeringDoel.naam, week: markeringDoel.weekKey, email: markeringDoel.email || '' });
    authFetch(`${BACKEND_URL}/Api/draaidagen-overzicht/week-detail-persoon?${params}`)
      .then((r) => leesJson(r, 'Weekdetail laden'))
      .then((d) => setAgendaDetail((prev) => ({ ...prev, [sleutel]: d })))
      .catch(() => setAgendaDetail((prev) => ({ ...prev, [sleutel]: { fout: true } })));
  }, [markeringDoel, agendaDetail, weekDetail]);
  const zelfdeDoel = (a, b) => Boolean(a && b && a.naam === b.naam && a.weekKey === b.weekKey && a.soort === b.soort);
  const toggleVast = (doel) => setMarkeringVast((v) => (zelfdeDoel(v, doel) ? null : doel));

  // Esc klapt het week-check-paneel dicht en wist de markering (ook vastgezet).
  useEffect(() => {
    if (!markeringVast && !weekCheckOpen) return undefined;
    const opEsc = (e) => {
      if (e.key !== 'Escape') return;
      const paneel = weekCheckOpen && document.getElementById(`weekcheck-paneel-${weekCheckOpen}`);
      const uitPaneel = Boolean(paneel && paneel.contains(document.activeElement));
      setMarkeringVast(null); setMarkeringHover(null); setMarkeringFocus(null); setWeekCheckOpen(null);
      // Toetsenbordgebruiker houdt zijn plek: focus terug op de badge die het paneel opende.
      if (uitPaneel && weekCheckKnop.current?.isConnected) weekCheckKnop.current.focus();
    };
    window.addEventListener('keydown', opEsc);
    return () => window.removeEventListener('keydown', opEsc);
  }, [markeringVast, weekCheckOpen]);

  const filterSelect = (label, value, onChange, opties) => (
    <Select size="small" value={value} displayEmpty onChange={(e) => onChange(e.target.value)}
      sx={{ minWidth: 130, fontFamily: FONT, fontSize: '0.82rem', bgcolor: '#fff' }}>
      <MenuItem value=""><em>{label}</em></MenuItem>
      {opties.map((o) => (Array.isArray(o)
        ? <MenuItem key={o[0]} value={o[0]} sx={{ fontSize: '0.82rem' }}>{o[1]}</MenuItem>
        : <MenuItem key={o} value={o} sx={{ fontSize: '0.82rem' }}>{o}</MenuItem>))}
    </Select>
  );

  // Week-check als inline paneel onder de weekkop (Joris, 13 okt): de oude
  // zwevende popover lag over de draaidagen heen, zodat je de markering niet zag
  // terwijl je een naam aanwees. Het paneel schuift de tabel omlaag en bedekt niets.
  const sluitWeekCheck = () => {
    setWeekCheckOpen(null);
    setMarkeringHover(null);
    setMarkeringFocus(null);
  };
  const toggleWeekCheck = (weekKey, knop) => {
    if (weekCheckOpen === weekKey) { sluitWeekCheck(); return; }
    weekCheckKnop.current = knop || null;
    setMarkeringHover(null);
    setMarkeringFocus(null);
    setWeekCheckOpen(weekKey);
  };
  // Verdwijnt het paneel of de aangewezen naam zonder mouseleave/blur (filter,
  // verversen), dan mag een hover/focus-voorproefje niet blijven hangen (Codex r1).
  // Een vastgezette markering blijft staan: die heeft zijn eigen wisbare chip.
  const kalenderModus = weergave === 'kalender' && scope !== 'buitenland';
  const paneelZichtbaar = Boolean(weekCheckOpen && weekChecks[weekCheckOpen] && !laden
    && (kalenderModus || groepen.some((g) => g.weekKey === weekCheckOpen)));
  useEffect(() => {
    if (weekCheckOpen && !laden && !paneelZichtbaar) setWeekCheckOpen(null);
    const nogInPaneel = (doel) => {
      if (!paneelZichtbaar || doel.weekKey !== weekCheckOpen) return false;
      const wc = weekChecks[doel.weekKey];
      return ['over', 'op_max', 'druk_met_agenda', 'ruimte'].some((k) => (wc[k] || []).some((i) => i.naam === doel.naam));
    };
    if (markeringHover && !nogInPaneel(markeringHover)) setMarkeringHover(null);
    if (markeringFocus && !nogInPaneel(markeringFocus)) setMarkeringFocus(null);
  }, [paneelZichtbaar, weekCheckOpen, laden, weekChecks, markeringHover, markeringFocus]);

  const weekCheckPaneel = (weekKey, weekLabel) => {
    const wc = weekChecks[weekKey];
    if (!wc || weekCheckOpen !== weekKey) return null;
    // Doorklik: wat is er bij deze persoon aan de hand — draaidagen +
    // agenda-afspraken van die week, externe gemarkeerd (Joris, 1 sep).
    const haalDetail = async (info) => {
      const sleutel = `${weekKey}:${info.naam}`;
      if (weekDetail[sleutel]) {
        setWeekDetail((prev) => ({ ...prev, [sleutel]: undefined }));
        return;
      }
      const id = (detailVerzoek.current[sleutel] || 0) + 1;
      detailVerzoek.current[sleutel] = id;
      setWeekDetail((prev) => ({ ...prev, [sleutel]: { laden: true } }));
      // Alleen schrijven als dit nog het actuele verzoek is én het detail nog
      // openstaat (laden); ingeklapt of vervangen = achterhaald, negeren.
      const zet = (waarde) => setWeekDetail((prev) => (
        detailVerzoek.current[sleutel] === id && prev[sleutel]?.laden ? { ...prev, [sleutel]: waarde } : prev));
      try {
        const params = new URLSearchParams({ naam: info.naam, week: weekKey, email: info.email || '' });
        const r = await authFetch(`${BACKEND_URL}/Api/draaidagen-overzicht/week-detail-persoon?${params}`);
        zet(await leesJson(r, 'Weekdetail laden'));
      } catch {
        zet({ fout: true });
      }
    };
    const DETAIL = (info) => {
      const d = weekDetail[`${weekKey}:${info.naam}`];
      if (!d) return null;
      if (d.laden) return <Typography sx={{ fontSize: 11.5, color: '#94a3b8' }}>Laden…</Typography>;
      if (d.fout) return <Typography sx={{ fontSize: 11.5, color: '#b91c1c' }}>Detail laden mislukt</Typography>;
      if (!(d.dagen || []).length) return <Typography sx={{ fontSize: 11.5, color: '#94a3b8' }}>Geen details gevonden.</Typography>;
      return (
        <Box sx={{ mt: 0.25, mb: 0.75, borderLeft: '2px solid #e2e8f0', pl: 1 }}>
          {d.dagen.map((dag) => (
            <Box key={dag.datum} sx={{ mb: 0.25 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#334155' }}>
                {new Date(`${dag.datum}T12:00:00`).toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })}
              </Typography>
              {(dag.draaidagen || []).map((naam, i) => (
                <Typography key={`dd${i}`} sx={{ fontSize: 11.5, color: '#0f172a' }}>🎬 {naam}</Typography>
              ))}
              {(dag.afspraken || []).map((a, i) => (
                <Typography key={`af${i}`} sx={{ fontSize: 11.5,
                  color: a.soort === 'extern' ? '#b91c1c' : '#64748b',
                  fontWeight: a.soort === 'extern' ? 700 : 400 }}>
                  {a.soort === 'extern' ? '🔴' : '·'} {a.tijd} {a.onderwerp}{a.soort === 'extern' ? ' (extern)' : ''}
                </Typography>
              ))}
            </Box>
          ))}
        </Box>
      );
    };
    // Naam = markeer zijn draaidagen in de tabel. Hover/focus = voorproefje,
    // klik/Enter = vastzetten (en het weekdetail openklappen), nog eens = los.
    const NAAM = (info, soort, klikbaar = false) => {
      const doel = { naam: info.naam, weekKey, soort, email: info.email || '' };
      const vast = zelfdeDoel(markeringVast, doel);
      // Het weekdetail volgt de vastgezette naam: vastzetten klapt alleen zijn
      // detail open (de rest dicht), losmaken klapt het weer in. Eén klik = één effect.
      const klik = () => {
        const eigen = `${weekKey}:${info.naam}`;
        toggleVast(doel);
        if (vast) {
          if (weekDetail[eigen]) setWeekDetail((prev) => ({ ...prev, [eigen]: undefined }));
          return;
        }
        setWeekDetail((prev) => Object.fromEntries(Object.entries(prev)
          .filter(([k]) => k === eigen || !k.startsWith(`${weekKey}:`))));
        if (klikbaar && !weekDetail[eigen]) haalDetail(info);
      };
      const telling = [
        `${info.draaidagen}/${info.max}`,
        info.optie ? `${info.optie} optie` : null,
        info.busy_days ? `${info.busy_days} ${info.busy_days === 1 ? 'dag' : 'dagen'} vol` : null,
      ].filter(Boolean).join(' · ');
      return (
        <Box key={`${soort}:${info.naam}`} sx={soort === 'ruimte' ? { display: 'inline-block', mr: 1.5 } : undefined}>
          <Typography component="span" role="button" tabIndex={0} aria-pressed={vast}
            data-testid={`weekcheck-naam-${soort}-${info.naam}`}
            onClick={klik}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); klik(); } }}
            onMouseEnter={() => setMarkeringHover(doel)}
            onMouseLeave={() => setMarkeringHover(null)}
            onFocus={() => setMarkeringFocus(doel)}
            onBlur={() => setMarkeringFocus(null)}
            sx={{ display: 'inline-block', fontSize: 12.5, lineHeight: 1.6, color: '#334155', cursor: 'pointer',
              borderRadius: 1, px: 0.5, mx: -0.5,
              ...(vast && { bgcolor: '#e2e8f0', fontWeight: 700 }),
              '&:hover': { bgcolor: vast ? '#e2e8f0' : '#e9eef4' },
              '&:focus-visible': { outline: '2px solid #6366f1', outlineOffset: 1 } }}>
            {info.naam}{info.rol ? ` (${info.rol})` : ''}
            <Box component="span" sx={{ color: '#94a3b8', fontWeight: 400, ml: 0.75 }}>{telling}</Box>
            {klikbaar && (
              <Box component="span" aria-hidden sx={{ color: '#94a3b8', ml: 0.5 }}>
                {weekDetail[`${weekKey}:${info.naam}`] ? '▾' : '▸'}
              </Box>
            )}
          </Typography>
          {klikbaar && DETAIL(info)}
        </Box>
      );
    };
    const kolommen = [
      { soort: 'over', titel: 'Over max', kleur: '#b91c1c', items: wc.over || [] },
      { soort: 'op_max', titel: 'Op max', kleur: '#b45309', items: wc.op_max || [] },
      { soort: 'druk', titel: 'Volle agenda', kleur: '#b45309', items: wc.druk_met_agenda || [], uitleg: 'dubbelcheck de afspraken' },
      { soort: 'ruimte', titel: 'Nog ruimte', kleur: '#166534', items: wc.ruimte || [] },
    ].filter((k) => k.items.length > 0 || k.soort === 'ruimte');
    return (
      <Box id={`weekcheck-paneel-${weekKey}`} data-testid="weekcheck-paneel" role="region"
        aria-label={`Planningsregels ${weekLabel || weekKey}`}
        sx={{ bgcolor: '#f1f5f9', borderRadius: 2, px: { xs: 1.5, md: 2 }, py: 1.25, mb: 1 }}>
        <Box sx={{ display: 'grid', columnGap: 3, rowGap: 1.25,
          gridTemplateColumns: { xs: '1fr', md: kolommen.map((k) => (k.soort === 'ruimte' ? 'minmax(0, 1.6fr)' : 'minmax(0, 1fr)')).join(' ') } }}>
          {kolommen.map((k) => (
            <Box key={k.soort} data-testid={`weekcheck-kolom-${k.soort}`} sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: k.kleur, mb: 0.25 }}>
                {k.titel}
                {k.uitleg && <Box component="span" sx={{ color: '#94a3b8', fontWeight: 400 }}> · {k.uitleg}</Box>}
              </Typography>
              {k.items.length
                ? k.items.map((i) => NAAM(i, k.soort, k.soort !== 'ruimte'))
                : <Typography sx={{ fontSize: 12.5, color: '#94a3b8' }}>Niemand, iedereen zit vol.</Typography>}
            </Box>
          ))}
        </Box>
        <Typography sx={{ fontSize: 10.5, color: '#a0aec0', mt: 1 }}>
          {weergave !== 'kalender' && 'Wijs een naam aan om zijn draaidagen hieronder te zien; klik om vast te zetten (Esc wist). '}
          Regels: Admin → Planning-regels (max draaidagen per week) · agenda = Outlook, ≥4 u afspraken op een dag telt als vol.
        </Typography>
      </Box>
    );
  };

  return (
    <Box ref={paginaRef} sx={{ width: '100%', maxWidth: presentatie ? 'none' : 1800, mx: 'auto',
      px: { xs: 2, sm: 3, md: 5 }, pt: presentatie ? 2 : { xs: 1.5, sm: 2 }, pb: 6,
      ...(presentatie && { bgcolor: '#fff', overflowY: 'auto', height: '100vh', zoom: 1.2 }) }}>
      {/* "Draaidagen" staat als tab in de kopbalk (PlanningTabs, 26 sep); alleen in de
          presentatiemodus (zonder kopbalk) blijft de grote titel staan. */}
      <PageHeader
        kaal={!presentatie}
        icon="videocam"
        title={presentatie
          ? (scope === 'buitenland' ? 'Buitenlandshoots' : 'Draaidagen')
          : (scope === 'buitenland' ? 'Buitenlandshoots — door te geven aan de reisverzekering' : null)}
        subtitle={!presentatie ? null : scope === 'buitenland'
          ? 'Deze shoots moeten worden doorgegeven aan de reisverzekering'
          : 'Alle geplande shoots met voorbereidings- en planningstatus'}
        rightActions={(
          <>
            <Button size="small" variant={weergave === 'kalender' ? 'contained' : 'outlined'}
              startIcon={<CalendarViewWeekIcon />}
              onClick={() => { const n = weergave === 'kalender' ? 'lijst' : 'kalender'; setWeergave(n); try { localStorage.setItem('draaidagen-weergave', n); } catch { /* privémodus */ } }}
              sx={{ textTransform: 'none', fontWeight: 700 }}>
              {weergave === 'kalender' ? 'Lijst' : 'Kalender'}
            </Button>
            <Button size="small" variant={scope === 'buitenland' ? 'contained' : 'outlined'}
              startIcon={<PublicIcon />}
              onClick={() => setScope(scope === 'buitenland' ? 'aankomend' : 'buitenland')}
              sx={{ textTransform: 'none', fontWeight: 700 }}>
              {scope === 'buitenland' ? 'Naar alle draaidagen' : 'Draaidagen in buitenland'}
            </Button>
            <Tooltip title={presentatie ? 'Presentatiemodus sluiten (of Esc)' : 'Presentatiemodus: fullscreen op een scherm, ververst zichzelf elke minuut'}>
              <Button size="small" variant={presentatie ? 'contained' : 'outlined'} onClick={togglePresentatie}
                sx={{ textTransform: 'none', fontWeight: 700 }}>
                📺 {presentatie ? 'Sluiten' : 'Presentatie'}
              </Button>
            </Tooltip>
            <Tooltip title="Vernieuwen">
              <IconButton size="small" onClick={() => laad(scope, true, rows.length > 0)}><RefreshIcon /></IconButton>
            </Tooltip>
          </>
        )}
      />

      {/* Filters */}
      <Stack direction="row" spacing={1} alignItems="center"
        sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1, ...(presentatie && { display: 'none' }) }}>
        {filterSelect('Crew', crewFilter, setCrewFilter, crewOpties)}
        {filterSelect('Project', projectFilter, setProjectFilter, projectOpties)}
        {filterSelect('PL', plFilter, setPlFilter, plOpties)}
        <TextField size="small" placeholder="Zoeken…" value={zoek} onChange={(e) => setZoek(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 220, bgcolor: '#fff' }} />
        {/* Vastgezette markering: altijd hier te wissen, ook als filters de week verbergen. */}
        {markeringVast && (
          <Chip size="small" data-testid="markering-vast"
            label={`${markeringVast.naam} gemarkeerd · Esc wist`}
            onDelete={() => setMarkeringVast(null)}
            sx={{ height: 24, fontSize: 11.5, bgcolor: '#f1f5f9', color: '#475569' }} />
        )}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" sx={{ color: '#cbd5e1', mr: 1.5 }}>
          sleep de randen van de kolomkoppen om te verbreden
        </Typography>
        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700 }}>
          {gefilterd.length} draaidag{gefilterd.length === 1 ? '' : 'en'}
        </Typography>
      </Stack>

      {fout && <Alert severity="error" sx={{ mb: 2 }}>{fout}</Alert>}
      {scope !== 'buitenland' && verborgenNogInOutlook.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>{verborgenNogInOutlook.length === 1 ? 'Eén draaidag is' : `${verborgenNogInOutlook.length} draaidagen zijn`} in de tool verwijderd (verborgen), maar de Outlook-afspraak bestaat nog.</strong>
          {' '}Zo staat hij wel in de agenda van de crew maar nergens in de planning. Zet terug als de dag doorgaat, of verwijder de afspraak in Outlook.
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {verborgenNogInOutlook.map((item) => (
              <Stack key={item.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{item.startDatum}</Typography>
                <Typography sx={{ fontSize: '0.8rem' }}>{item.naam}</Typography>
                <Button size="small" variant="outlined" color="warning" disabled={terugzetBezig === item.id}
                  onClick={() => zetTerug(item)} sx={{ textTransform: 'none', fontWeight: 700, py: 0 }}>
                  {terugzetBezig === item.id ? 'Bezig…' : 'Terugzetten in planning'}
                </Button>
              </Stack>
            ))}
          </Stack>
        </Alert>
      )}
      {laden ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>
      ) : weergave === 'kalender' && scope !== 'buitenland' ? (
        <>
        {weekCheckOpen && weekCheckPaneel(weekCheckOpen,
          `week van ${new Date(`${weekCheckOpen}T12:00:00`).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' })}`)}
        <DraaidagenKalender
          rows={gefilterd}
          weekChecks={weekChecks}
          vandaagISO={vandaagISO}
          filterActief={Boolean(crewFilter || plFilter || projectFilter || zoek.trim())}
          onKies={setGekozen}
          onWeekKlik={(anchor, weekKey) => toggleWeekCheck(weekKey, anchor)}
        />
        </>
      ) : groepen.length === 0 ? (
        <Alert severity="info" icon={<EventNoteIcon />}>Geen draaidagen gevonden binnen deze filters.</Alert>
      ) : (
        groepen.map((g) => (
          <Box key={g.label} sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1}
              sx={{ mb: 0.75, position: 'sticky', top: 0, zIndex: 3, bgcolor: '#f8f9fb', py: 0.5 }}>
              <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: '0.95rem', color: '#2d3335' }}>
                {g.label}
              </Typography>
              <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700 }}>{g.rows.length}</Typography>
              {g.weekKey && weekChecks[g.weekKey] && (
                <WeekRegelBadge wc={weekChecks[g.weekKey]} weekLabel={g.label}
                  open={weekCheckOpen === g.weekKey} paneelId={`weekcheck-paneel-${g.weekKey}`}
                  onOpen={(knop) => toggleWeekCheck(g.weekKey, knop)} />
              )}
            </Stack>
            {g.weekKey && weekCheckPaneel(g.weekKey, g.label)}

            <Box sx={{ border: '1px solid #e8edf2', borderRadius: 2, overflowX: 'auto', bgcolor: '#fff' }}>
              {/* kolomkoppen */}
              <Box sx={{
                display: 'grid', alignItems: 'center',
                gridTemplateColumns: rijGrid,
                gap: 1, px: 1.5, py: 0.5, borderBottom: '1px solid #eef2f6', bgcolor: '#fafbfc',
              }}>
                {[['', null], ['Weekdag', null], ['Dgn', null], ['Draaidag', 'draaidag'], ['Project', 'project'],
                  ['Opdrachtgever', 'opdrachtgever'], ['PL', 'pl'], ['Crew', 'crew'], ['Planningstatus', 'status']].map(([h, kol], i) => (
                  <Box key={i} sx={{ position: 'relative', minWidth: 0, pr: kol ? 1 : 0 }}>
                    <Typography variant="caption" noWrap sx={{ color: '#94a3b8', fontWeight: 700, fontSize: '0.68rem', display: 'block' }}>{h}</Typography>
                    {kol && (
                      <Box onMouseDown={startSleep(kol)}
                        onDoubleClick={() => setKolomBreedte((b) => ({ ...b, [kol]: STANDAARD_BREEDTES[kol] }))}
                        title="Sleep om de kolom te verbreden · dubbelklik = standaard"
                        sx={{ position: 'absolute', top: -6, bottom: -6, right: -6, width: 12, cursor: 'col-resize', zIndex: 2,
                          '&:hover': { bgcolor: '#c7d2fe55', borderRight: '2px solid #6366f1' } }} />
                    )}
                  </Box>
                ))}
              </Box>

              {g.rows.map((r, ri) => {
                // Agenda-stijl (zoals agenda-apps): de datum staat één keer per
                // dag; vandaag krijgt een accent; tussen dagen een duidelijker
                // scheiding dan tussen shoots op dezelfde dag.
                // Lokale datum, niet toISOString (=UTC): tussen 00:00 en 02:00
                // zomertijd zou 'vandaag' anders nog gisteren zijn (Codex r11).
                const nu = new Date();
                const vandaag = `${nu.getFullYear()}-${String(nu.getMonth() + 1).padStart(2, '0')}-${String(nu.getDate()).padStart(2, '0')}`;
                const isVerleden = String(r.eindDatum || r.startDatum || '9999') < vandaag;
                const isVandaag = String(r.startDatum || '') === vandaag;
                const eersteVanDag = ri === 0 || datumSleutel(g.rows[ri - 1]) !== datumSleutel(r);
                const mk = markering ? markering.rijen.get(r.id) : null;
                const mkToon = MARKERING_TOON[markering?.soort] || MARKERING_TOON.op_max;
                // Alleen binnen de gemarkeerde week dimmen; andere weken blijven rustig staan.
                const gedimd = Boolean(markering && !mk && rijInWeek(r, markering.weekKey));
                const crewIndex = mk?.crewNaam ? r.crew.indexOf(mk.crewNaam) : -1;
                const chipSx = (c) => (mk && c === mk.crewNaam
                  ? { bgcolor: mkToon.chip, color: mkToon.fg, fontWeight: 800, boxShadow: `inset 0 0 0 1px ${mkToon.fg}` }
                  : { bgcolor: '#f1f5f9', color: '#334155' });
                return (
                <Box key={r.id}
                  onClick={() => setGekozen(r.id)}
                  data-testid={`draaidag-rij-${r.id}`}
                  data-gemarkeerd={mk ? 'ja' : gedimd ? 'gedimd' : undefined}
                  sx={{
                    display: 'grid', alignItems: 'center',
                    gridTemplateColumns: rijGrid,
                    gap: 1, px: 1.5, py: 0.9, cursor: 'pointer',
                    borderBottom: '1px solid #f4f7fa',
                    ...(eersteVanDag && ri > 0 && { borderTop: '2px solid #e8edf2' }),
                    borderLeft: `3px solid ${DOT_KLEUR[r.dot] || '#94a3b8'}`,
                    ...(isVandaag && { bgcolor: '#f5f7ff' }),
                    ...(isVerleden && { opacity: 0.55, bgcolor: '#fafafa', filter: 'grayscale(0.35)' }),
                    ...(mk && { bgcolor: mkToon.bg, opacity: 1, filter: 'none' }),
                    ...(gedimd && { opacity: 0.4 }),
                    transition: 'opacity 120ms, background-color 120ms',
                    '&:hover': { bgcolor: mk ? mkToon.bg : '#f8fafc', opacity: 1, filter: 'none' }, '&:last-child': { borderBottom: 0 },
                  }}>
                  <StatusDot dot={r.dot} foutmelding={r.foutmelding} voorbereidingsstatus={r.voorbereidingsstatus} />
                  {mk && mk.dagen.length > 0 ? (
                    <Tooltip title={`Volle agenda op ${mk.dagen.map((d) => weekdagLabel(d)).join(', ')} — dubbelcheck de afspraken`}>
                      <Typography data-testid="volle-agenda-dag"
                        sx={{ fontSize: '0.8rem', fontWeight: 800, color: mkToon.fg, bgcolor: mkToon.chip,
                          borderRadius: 1, px: 0.5, mx: -0.5, width: 'fit-content' }}>
                        {/* Het shootbereik blijft staan; bij een meerdaagse shoot noemt de tooltip de volle dag(en). */}
                        {isVandaag ? 'Vandaag' : weekdagLabel(r.startDatum, r.eindDatum && r.eindDatum > r.startDatum ? r.eindDatum : r.dagen)}
                        {dagenVanRij(r).length > 1 ? ` · vol: ${mk.dagen.map((d) => WEEKDAG_KORT[new Date(`${d}T12:00:00`).getDay()].toLowerCase()).join(', ')}` : ''}
                      </Typography>
                    </Tooltip>
                  ) : (
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: isVandaag ? 800 : 700,
                    color: isVandaag ? '#4338ca' : (eersteVanDag || Number(r.dagen) > 1 || mk ? '#374151' : '#cbd5e1') }}>
                    {!r.startDatum ? '—' : eersteVanDag || Number(r.dagen) > 1 || mk ? (isVandaag ? 'Vandaag' : weekdagLabel(r.startDatum, r.eindDatum && r.eindDatum > r.startDatum ? r.eindDatum : r.dagen)) : '·'}
                  </Typography>
                  )}
                  <Typography sx={{ fontSize: '0.8rem', color: '#6b7280' }}>{r.dagen}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                    <Tooltip title={r.materiaal ? 'Materiaallijst ingevuld' : 'Nog geen materiaallijst (Materialen reserveren)'}>
                      <Typography component="span" sx={{ fontSize: '0.85rem', flexShrink: 0, filter: r.materiaal ? 'none' : 'grayscale(1) opacity(0.35)' }}>
                        🎒
                      </Typography>
                    </Tooltip>
                    <Tooltip title={`${r.origineleTitel || r.titel}${r.projectnaam ? `\nProject: ${r.projectnaam}` : ''}${r.opdrachtgever ? `\nOpdrachtgever: ${r.opdrachtgever}` : ''}`}
                      componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line', fontSize: '0.75rem' } } }}>
                      <Typography noWrap sx={{ fontSize: '0.82rem', color: '#1f2937', minWidth: 0 }}>{r.titel}</Typography>
                    </Tooltip>
                    {mk?.optie && (
                      <Tooltip title="Optie: nog niet bevestigd, maar telt mee in het maximum">
                        <Typography component="span" data-testid="optie-telt-mee"
                          sx={{ fontSize: '0.68rem', fontWeight: 700, color: mkToon.fg, flexShrink: 0,
                            border: `1px dashed ${mkToon.fg}`, borderRadius: 1, px: 0.5, lineHeight: 1.4 }}>
                          optie telt mee
                        </Typography>
                      </Tooltip>
                    )}
                  </Box>
                  {r.projectnummer ? (
                    <Stack spacing={0.4} alignItems="center" sx={{ minWidth: 0, overflow: 'hidden', width: 'fit-content' }}>
                      <Chip size="small" label={r.projectnummer}
                        onClick={(e) => { e.stopPropagation(); r.projectId && navigate(`/projects/${r.projectId}`); }}
                        sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: '#eef2ff', color: '#3730a3' }} />
                      {stoplichten[r.projectId] ? (
                        <Box sx={{ px: 0.5, py: 0.2, borderRadius: 2, bgcolor: '#f8fafc', lineHeight: 0 }}>
                          <StoplichtRij status={stoplichten[r.projectId]} size={6} />
                        </Box>
                      ) : null}
                    </Stack>
                  ) : <Typography sx={{ fontSize: '0.8rem', color: '#cbd5e1' }}>—</Typography>}
                  <Typography noWrap sx={{ fontSize: '0.8rem', color: '#374151', minWidth: 0 }}>{r.opdrachtgever || '—'}</Typography>
                  {r.plUitTitel ? (
                    <Tooltip title={r.plZeker
                      ? `Uit de initialen in de agendatitel (${r.plInitialen}) — geen PL in Airtable`
                      : `Initialen ${r.plInitialen} in de agendatitel${r.plKandidaten?.length ? `, kan zijn: ${r.plKandidaten.join(', ')}` : ''} — geen PL in Airtable`}>
                      <Typography noWrap sx={{ fontSize: '0.8rem', color: r.plZeker ? '#374151' : '#b45309', minWidth: 0, fontStyle: 'italic' }}>{r.pl}</Typography>
                    </Tooltip>
                  ) : (
                    <Typography noWrap sx={{ fontSize: '0.8rem', color: '#374151', minWidth: 0 }}>{r.pl || '—'}</Typography>
                  )}
                  <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5, minWidth: 0 }}>
                    {/* Gemarkeerde persoon buiten de eerste drie: wissel hem naar voren. */}
                    {(crewIndex >= 3 ? [...r.crew.slice(0, 2), mk.crewNaam] : r.crew.slice(0, 3)).map((c) => (
                      <Chip key={c} size="small" label={c} data-nadruk={mk && c === mk.crewNaam ? 'ja' : undefined}
                        sx={{ height: 20, fontSize: 11, ...chipSx(c) }} />
                    ))}
                    {r.crew.length > 3 && (
                      <Tooltip title={r.crew.filter((c, i) => (crewIndex >= 3 ? i >= 2 && c !== mk.crewNaam : i >= 3)).join(', ')} arrow>
                        <Chip size="small" label={`+${r.crew.length - 3}`} sx={{ height: 20, fontSize: 11, bgcolor: '#f1f5f9', color: '#64748b' }} />
                      </Tooltip>
                    )}
                    {openPerRij.has(r.id) && (() => {
                      const o = openPerRij.get(r.id);
                      const weken = wekenVan(o.datums);
                      return (
                        <OpenRolSignaal rij={r} open={o.open} onbekend={o.onbekend} datums={o.datums}
                          weken={wekenStatus(weken)} onNodig={() => laadBeschikbaarheid(weken)} />
                      );
                    })()}
                    {mk?.titelNaam && (
                      <Tooltip title="Externe crew uit de agendatitel (niet gekoppeld in Airtable)">
                        <Chip size="small" label={mk.titelNaam} data-nadruk="ja"
                          sx={{ height: 20, fontSize: 11, bgcolor: mkToon.chip, color: mkToon.fg, fontWeight: 800,
                            boxShadow: `inset 0 0 0 1px ${mkToon.fg}`, fontStyle: 'italic' }} />
                      </Tooltip>
                    )}
                  </Stack>
                  <PlanningstatusBadge status={r.planningstatus} />
                </Box>
                );
              })}
            </Box>
          </Box>
        ))
      )}

      <DraaidagDetailDrawer
        draaidagId={gekozen}
        open={Boolean(gekozen)}
        onClose={() => setGekozen(null)}
        onBijgewerkt={() => laad(scope, true, true)}
        portalContainer={presentatie ? paginaRef.current : undefined}
      />
    </Box>
  );
}
